import React, { useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Flex, Text } from "@jsq/ds/core";
import { FilterPanel } from "./components/FilterPanel";
import { TransactionDetailDrawer } from "./components/TransactionDetailDrawer";
import { TransactionTable } from "./components/TransactionTable";
import { TransactionLookupFilters, TransactionRowItem } from "./types";

/** Read entry URL once (ArenaDetail deep-link). Filters never write back to history. */
const parseQueryParams = (search: string): Partial<TransactionLookupFilters> => {
  const params = new URLSearchParams(search);
  const arenaId = params.get("arenaId") || "";
  const entityIdParam = params.get("entityId");
  const entityId = entityIdParam ? parseInt(entityIdParam, 10) : undefined;
  const includeDeleted = params.get("includeDeleted") === "true";
  const glDateFrom = params.get("glDateFrom") || undefined;
  const glDateTo = params.get("glDateTo") || undefined;
  const effectiveDateFrom = params.get("effectiveDateFrom") || undefined;
  const effectiveDateTo = params.get("effectiveDateTo") || undefined;
  const status = params.get("status") || undefined;
  const batchSourceObjectId = params.get("batchId") || undefined;
  const glTransactionType = params.get("txnType") || undefined;
  const quickSearch = params.get("quickSearch") || undefined;
  const hasMappingParam = params.get("hasMapping");
  const hasMapping =
    hasMappingParam === "true" ? true : hasMappingParam === "false" ? false : undefined;

  return {
    arenaId,
    entityId,
    includeDeleted,
    glDateFrom,
    glDateTo,
    effectiveDateFrom,
    effectiveDateTo,
    status,
    batchSourceObjectId,
    glTransactionType,
    hasMapping,
    quickSearch,
  };
};

export const TransactionLookup: React.FC = () => {
  const location = useLocation();
  const initialParams = parseQueryParams(location.search);

  const [filters, setFilters] = useState<TransactionLookupFilters>({
    arenaId: initialParams.arenaId || "",
    entityId: initialParams.entityId,
    includeDeleted: initialParams.includeDeleted || false,
    glDateFrom: initialParams.glDateFrom,
    glDateTo: initialParams.glDateTo,
    effectiveDateFrom: initialParams.effectiveDateFrom,
    effectiveDateTo: initialParams.effectiveDateTo,
    status: initialParams.status,
    batchSourceObjectId: initialParams.batchSourceObjectId,
    glTransactionType: initialParams.glTransactionType,
    hasMapping: initialParams.hasMapping,
    quickSearch: initialParams.quickSearch,
  });

  const [selectedTransaction, setSelectedTransaction] = useState<TransactionRowItem | null>(
    null,
  );

  const updateFilters = useCallback((newFilters: Partial<TransactionLookupFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const handleRowClick = useCallback((transaction: TransactionRowItem) => {
    setSelectedTransaction(transaction);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setSelectedTransaction(null);
  }, []);

  return (
    <Flex
      flexDirection="column"
      style={{
        padding: "24px",
        gap: "24px",
        backgroundColor: "#ffffff",
        minHeight: "100%",
      }}
    >
      <Text style={{ fontSize: "24px", fontWeight: 600 }}>Transaction Lookup</Text>

      <FilterPanel filters={filters} onFilterChange={updateFilters} />

      <TransactionTable filters={filters} onRowClick={handleRowClick} />

      <TransactionDetailDrawer transaction={selectedTransaction} onClose={handleCloseDrawer} />
    </Flex>
  );
};

export default TransactionLookup;
